import { useState } from 'react';
import { motion, useReducedMotion, AnimatePresence } from 'framer-motion';
import { Trophy, Medal, Star, Zap, Crown } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { interpolate } from '../utils/cabinI18n';
import { useLeaderboard } from '../hooks/cabin/useLeaderboard';
import { useAuth } from '../contexts/AuthContext';
import CabinErrorState from '../components/cabin/CabinErrorState';

const PERIODS = [
  { key: 'weekly',  label_tr: 'Haftalık',    label_en: 'Weekly'    },
  { key: 'monthly', label_tr: 'Aylık',       label_en: 'Monthly'   },
  { key: 'alltime', label_tr: 'Tüm Zamanlar',label_en: 'All Time'  },
];

function RankIcon({ rank }) {
  if (rank === 1) return <Crown size={20} className="text-amber-400" aria-hidden="true" strokeWidth={2.5} />;
  if (rank === 2) return <Medal size={20} className="text-gray-400" aria-hidden="true" strokeWidth={2.5} />;
  if (rank === 3) return <Medal size={20} className="text-amber-600" aria-hidden="true" strokeWidth={2.5} />;
  return (
    <span className="text-sm font-black text-[var(--text-muted)] tabular-nums w-5 text-center">{rank}</span>
  );
}

function RankBadge({ rank }) {
  if (rank === 1) return (
    <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-400 flex items-center justify-center text-[9px] font-black text-white shadow">1</span>
  );
  if (rank === 2) return (
    <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-gray-400 flex items-center justify-center text-[9px] font-black text-white shadow">2</span>
  );
  if (rank === 3) return (
    <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-600 flex items-center justify-center text-[9px] font-black text-white shadow">3</span>
  );
  return null;
}

function AvatarCircle({ name, rank }) {
  const initial = name?.[0]?.toUpperCase() || '?';
  const gradients = [
    'from-sky-400 to-blue-600',
    'from-emerald-400 to-green-600',
    'from-violet-400 to-purple-600',
    'from-rose-400 to-red-600',
    'from-amber-400 to-yellow-600',
  ];
  const gradient = gradients[(name?.charCodeAt(0) || 0) % gradients.length];

  return (
    <div className="relative shrink-0">
      <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center`}>
        <span className="text-white text-sm font-black">{initial}</span>
      </div>
      <RankBadge rank={rank} />
    </div>
  );
}

function SkeletonRow() {
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl animate-pulse">
      <div className="w-5 h-5 bg-gray-200 rounded shrink-0" />
      <div className="w-10 h-10 rounded-full bg-gray-200 shrink-0" />
      <div className="flex-1 space-y-1.5">
        <div className="h-3.5 bg-gray-200 rounded w-2/5" />
        <div className="h-2.5 bg-gray-200 rounded w-1/4" />
      </div>
      <div className="w-14 h-5 bg-gray-200 rounded" />
    </div>
  );
}

function LeaderboardRow({ entry, isCurrentUser, t, lang: _lang, shouldReduce }) {
  const rowBg = isCurrentUser
    ? 'bg-sky-50 border-2 border-sky-300 shadow-[0_2px_0_0_#BAE6FD]'
    : entry.rank <= 3
    ? 'bg-amber-50 border-2 border-amber-200'
    : 'bg-white border-2 border-transparent hover:border-gray-200';

  return (
    <motion.div
      layout
      initial={shouldReduce ? {} : { opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: Math.min(entry.rank * 0.03, 0.6) }}
      className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${rowBg}`}
      data-testid={`leaderboard-row-${entry.rank}`}
      aria-current={isCurrentUser ? 'true' : undefined}
    >
      {/* Rank icon */}
      <div className="w-5 flex items-center justify-center shrink-0">
        <RankIcon rank={entry.rank} />
      </div>

      {/* Avatar — use displayName initial for consistent first-name letter */}
      <AvatarCircle name={entry.displayName || entry.name} rank={entry.rank} />

      {/* Name + level */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className={`text-sm font-bold truncate ${isCurrentUser ? 'text-sky-700' : 'text-[var(--text-main)]'}`}>
            {entry.displayName || entry.name}
            {isCurrentUser && (
              <span className="ml-1.5 text-[10px] font-black text-sky-600 bg-sky-100 px-1.5 py-0.5 rounded-full">
                {t('cabin.gamification.leaderboard.you') || 'Sen'}
              </span>
            )}
          </p>
        </div>
        <p className="text-xs text-[var(--text-muted)]">
          {t('cabin.gamification.leaderboard.level') || 'Seviye'} {entry.level}
        </p>
      </div>

      {/* Stats */}
      <div className="flex flex-col items-end gap-0.5 shrink-0">
        <span className="flex items-center gap-1 text-xs font-black text-amber-600">
          <Zap size={11} aria-hidden="true" />
          {(entry.total_xp ?? 0).toLocaleString()}
        </span>
        <span className="flex items-center gap-1 text-xs font-bold text-sky-600">
          <Star size={11} aria-hidden="true" />
          {(entry.total_miles ?? 0).toLocaleString()}
        </span>
      </div>
    </motion.div>
  );
}

export default function CabinLeaderboard() {
  const { t, lang } = useLanguage();
  const shouldReduce = useReducedMotion();
  const { user } = useAuth();
  const [activePeriod, setActivePeriod] = useState('weekly');

  const { data, isLoading, isError, refetch } = useLeaderboard(activePeriod);

  const entries = data?.entries || [];
  const yourRank = data?.your_rank ?? null;
  const yourXp = data?.your_xp ?? null;
  const yourMiles = data?.your_miles ?? null;

  const currentUserId = user?._id || user?.id;

  // Derive yourXp/yourMiles from the matched row when hook didn't provide them
  const currentUserEntry = currentUserId
    ? entries.find((e) => e.user_id === currentUserId)
    : null;
  const resolvedYourXp = yourXp ?? currentUserEntry?.total_xp ?? null;
  const resolvedYourMiles = yourMiles ?? currentUserEntry?.total_miles ?? null;
  // If hook provides yourRank use it; fall back to matched entry rank
  const resolvedYourRank = yourRank ?? currentUserEntry?.rank ?? null;

  if (isError && !isLoading) {
    return (
      <CabinErrorState
        onRetry={refetch}
        backPath="/programs/kabin-memurlugu"
        testId="leaderboard-error"
      />
    );
  }

  return (
    <div className="max-w-xl mx-auto px-4 py-6" data-testid="cabin-leaderboard-page">
      {/* Page title */}
      <div className="flex items-center gap-2 mb-6">
        <Trophy size={22} className="text-amber-500" aria-hidden="true" strokeWidth={2.5} />
        <h1 className="text-xl font-black text-[var(--text-main)]">
          {t('cabin.gamification.leaderboard.title') || 'Liderlik Tablosu'}
        </h1>
      </div>

      {/* Your rank summary — only render when we have a rank */}
      {resolvedYourRank != null && (
        <motion.div
          initial={shouldReduce ? {} : { opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-5 p-4 bg-gradient-to-r from-sky-500 to-blue-600 rounded-2xl border-2 border-sky-400 shadow-[0_4px_0_0_#2563EB] text-white"
          data-testid="leaderboard-my-rank"
        >
          <p className="text-xs font-bold opacity-80 uppercase tracking-wide">
            {t('cabin.gamification.leaderboard.my_rank_label') || 'Sıralamadaki Yerin'}
          </p>
          <div className="flex items-center justify-between mt-1">
            <p className="text-2xl font-black">
              #{resolvedYourRank}
            </p>
            <div className="flex items-center gap-3">
              {resolvedYourXp != null && resolvedYourXp > 0 && (
                <span className="flex items-center gap-1 text-sm font-bold">
                  <Zap size={14} aria-hidden="true" />
                  {(resolvedYourXp ?? 0).toLocaleString()} XP
                </span>
              )}
              {resolvedYourMiles != null && resolvedYourMiles > 0 && (
                <span className="flex items-center gap-1 text-sm font-bold">
                  <Star size={14} aria-hidden="true" />
                  {(resolvedYourMiles ?? 0).toLocaleString()} {t('cabin.gamification.leaderboard.miles_unit') || 'Mil'}
                </span>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* Period tabs */}
      <div
        className="flex gap-1 mb-5 p-1 bg-gray-100 rounded-xl"
        role="tablist"
        aria-label={t('cabin.gamification.leaderboard.period_tabs_aria') || 'Dönem seçimi'}
      >
        {PERIODS.map((period) => (
          <button
            key={period.key}
            role="tab"
            aria-selected={activePeriod === period.key}
            aria-controls={`leaderboard-panel-${period.key}`}
            onClick={() => setActivePeriod(period.key)}
            data-testid={`period-tab-${period.key}`}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-bold transition-all duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] ${
              activePeriod === period.key
                ? 'bg-white text-[var(--brand-primary)] shadow-sm border border-gray-200'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {lang === 'tr' ? period.label_tr : period.label_en}
          </button>
        ))}
      </div>

      {/* Leaderboard list */}
      <div
        id={`leaderboard-panel-${activePeriod}`}
        role="tabpanel"
        aria-label={
          lang === 'tr'
            ? PERIODS.find((p) => p.key === activePeriod)?.label_tr
            : PERIODS.find((p) => p.key === activePeriod)?.label_en
        }
        className="bg-white rounded-2xl border-2 border-gray-200 shadow-[0_4px_0_0_#E5E5E5] overflow-hidden"
      >
        <AnimatePresence mode="wait">
          {isLoading ? (
            <motion.div
              key="skeleton"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="divide-y divide-gray-100 p-2"
            >
              {Array.from({ length: 5 }).map((_, i) => (
                <SkeletonRow key={i} />
              ))}
            </motion.div>
          ) : isError ? (
            <motion.div
              key="error"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="p-8 text-center text-[var(--text-muted)] text-sm"
            >
              {t('cabin.gamification.leaderboard.error') || 'Sıralama yüklenemedi'}
            </motion.div>
          ) : entries.length === 0 ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="p-8 text-center text-[var(--text-muted)] text-sm"
            >
              {t('cabin.gamification.leaderboard.empty') || 'Henüz sıralama yok'}
            </motion.div>
          ) : (
            <motion.div
              key={activePeriod}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col gap-1 p-2"
            >
              {entries.map((entry) => (
                <LeaderboardRow
                  key={entry.user_id}
                  entry={entry}
                  isCurrentUser={currentUserId && entry.user_id === currentUserId}
                  t={t}
                  lang={lang}
                  shouldReduce={shouldReduce}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Caption */}
      <p className="mt-3 text-center text-xs text-[var(--text-muted)]">
        {interpolate(
          t('cabin.gamification.leaderboard.top_n') || 'İlk {n} kullanıcı gösteriliyor',
          { n: entries.length }
        )}
      </p>
    </div>
  );
}
